'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import { cn } from '@/core/utils/cn';
import { useLocalization } from '@/core/hooks/useLocalization';
import {
  Images,
  MapPin,
  Star,
  Clock,
  CalendarX2,
  ChevronLeft,
  Share2,
  Heart,
  Building2,
  Info,
  Check,
  Shield,
} from 'lucide-react';
import { HotelRoomTypes } from './HotelRoomTypes';
import { PhotoLightbox } from './PhotoLightbox';
import { SimilarHotels } from './HotelDetailSimilar';
import { HotelDetailContactMap } from './HotelDetailContactMap';
import { useHotelRoomTypes, useHotelBySlug, usePublicHotels, useHotelReviews } from '../hooks/useHotels';
import type { HotelRoomTypeData } from '../types/hotels.types';
import { computeGST } from '@/core/constants/taxes';
import { getHotelGallerySeeds } from './hotelContent';
import { useTenantContext } from '@/core/providers/TenantProvider';
import { createQueryString, withQuery, PUBLIC_RESULTS_QUERY_KEYS, PUBLIC_BOOKING_QUERY_KEYS } from '../hotel-routing';
import { useWishlist } from '../hooks/useWishlist';
import { DateRangePicker } from '@/shared/forms/DateRangePicker';
import { Select } from '@/shared/ui';
import { HotelDiningSection } from '@/modules/dining';
import { useTranslation } from '@/core/i18n';

/* -------------------------------------------------------------------------- */
/*  Types                                                                      */
/* -------------------------------------------------------------------------- */

interface HotelDetailProps {
  slug: string;
}

/* -------------------------------------------------------------------------- */
/*  Sub-components                                                             */
/* -------------------------------------------------------------------------- */

function StarRating({ rating, size = 14 }: { rating: number; size?: number }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} size={size} className={cn(i < rating ? 'fill-hms-gold text-hms-gold' : 'fill-transparent text-hms-textDim/40')} />
      ))}
    </div>
  );
}

/* roomTypeOptions is computed from API data inside the component */

/* -------------------------------------------------------------------------- */
/*  Main component                                                             */
/* -------------------------------------------------------------------------- */

export function HotelDetail({ slug }: HotelDetailProps) {
  const { t } = useTranslation();
  const { formatMoney } = useLocalization();
  const { isDemo } = useTenantContext();
  const { data: roomTypesData } = useHotelRoomTypes();
  const hotelRoomTypes = useMemo(
    () => (((roomTypesData as unknown as Record<string, unknown>)?.data ?? []) as HotelRoomTypeData[]),
    [roomTypesData],
  );
  const roomTypeOptions = useMemo(() => hotelRoomTypes.map((r) => ({ value: r.slug, label: r.name })), [hotelRoomTypes]);
  const searchParams = useSearchParams();
  const [checkIn, setCheckIn] = useState(searchParams.get('checkIn') || '');
  const [checkOut, setCheckOut] = useState(searchParams.get('checkOut') || '');
  const [guests, setGuests] = useState(searchParams.get('guests') || '2');
  const [roomType, setRoomType] = useState(searchParams.get('room') || 'standard');
  const { isSaved, toggleSave } = useWishlist();
  const [shareStatus, setShareStatus] = useState<'idle' | 'copied' | 'failed'>('idle');
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [showAllPhotos, setShowAllPhotos] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [reviewFilter, setReviewFilter] = useState(0);
  const [reviewSort, setReviewSort] = useState<'newest' | 'highest' | 'lowest'>('newest');

  const handleShare = useCallback(() => {
    if (shareStatus === 'copied') return;
    try {
      navigator.clipboard.writeText(window.location.href);
      setShareStatus('copied');
    } catch {
      setShareStatus('failed');
    }
  }, [shareStatus]);

  const handleRoomSelect = useCallback((slug: string) => {
    setRoomType(slug);
  }, []);

  const selectedRoom = useMemo(
    () => hotelRoomTypes.find((r) => r.slug === roomType) ?? hotelRoomTypes[0] ?? null,
    [roomType, hotelRoomTypes],
  );
  const { data: hotel } = useHotelBySlug(slug);
  const { data: apiReviews } = useHotelReviews(slug);
  const [todayStr, setTodayStr] = useState('');
  // eslint-disable-next-line react-hooks/set-state-in-effect -- client-only initialization from browser Date API
  useEffect(() => { setTodayStr(new Date().toISOString().split('T')[0]); }, []);
  // minCheckOut handled internally by DateRangePicker
  const resultsQuery = useMemo(() => {
    const params: Record<string, string> = { checkIn, checkOut, guests };
    for (const key of PUBLIC_RESULTS_QUERY_KEYS) {
      const v = searchParams.get(key);
      if (v && !(key in params)) params[key] = v;
    }
    return createQueryString(params);
  }, [searchParams, checkIn, checkOut, guests]);
  const reserveHref = useMemo(() => {
    const params: Record<string, string> = { checkIn, checkOut, guests, room: roomType };
    for (const key of PUBLIC_BOOKING_QUERY_KEYS) {
      const v = searchParams.get(key);
      if (v && !(key in params)) params[key] = v;
    }
    return withQuery(`/hotels/${slug}/book`, createQueryString(params));
  }, [searchParams, slug, checkIn, checkOut, guests, roomType]);
  const hotelAmenities = useMemo(
    () => hotel?.amenities ?? [],
    [hotel],
  );
  const hotelReviewsAll = useMemo(
    () => (apiReviews ?? []).map((r) => ({ name: r.author, rating: r.rating, date: r.date, verified: r.verified, text: r.text })),
    [apiReviews],
  );
  const hotelGallerySeeds = useMemo(
    () => hotel ? getHotelGallerySeeds(hotel.name, isDemo) : [],
    [hotel, isDemo],
  );
  const galleryImages = useMemo(
    () => hotel ? [hotel.image, ...hotelGallerySeeds.slice(0, 2)] : [],
    [hotel, hotelGallerySeeds],
  );
  const filteredReviews = useMemo(() => {
    let list = reviewFilter > 0
      ? hotelReviewsAll.filter((r) => r.rating === reviewFilter)
      : [...hotelReviewsAll];
    if (reviewSort === 'highest') list.sort((a, b) => b.rating - a.rating);
    else if (reviewSort === 'lowest') list.sort((a, b) => a.rating - b.rating);
    return list;
  }, [reviewFilter, reviewSort, hotelReviewsAll]);
  const visibleReviews = useMemo(
    () => (showAllReviews ? filteredReviews : filteredReviews.slice(0, 3)),
    [showAllReviews, filteredReviews],
  );
  const allLightboxPhotos = useMemo(
    () => hotel ? [hotel.image, ...hotelGallerySeeds] : [],
    [hotel, hotelGallerySeeds],
  );
  const { data: allHotelsForSimilar } = usePublicHotels();
  const similarHotels = useMemo(
    () => {
      if (!hotel || !allHotelsForSimilar) return [];
      return allHotelsForSimilar.filter((h) => h.id !== hotel.id && (h.location.includes(hotel.location.split(',')[0]) || h.type === hotel.type)).slice(0, 4);
    },
    [hotel, allHotelsForSimilar],
  );
  const descriptionParagraphs = useMemo(() => hotel ? [
    `${hotel.name} is a ${hotel.stars}-star ${hotel.type.toLowerCase()} in ${hotel.location}, designed for travelers who want a polished stay with strong service, modern comforts, and easy access to the city's most important business and leisure districts.`,
    `Guests can expect thoughtfully designed rooms, a generous mix of dining and wellness amenities, and a stay experience that balances convenience with a premium hospitality feel for couples, families, and business guests alike.`,
  ] : [], [hotel]);

  useEffect(() => {
    if (!checkIn || !checkOut) return;
    if (new Date(checkOut).getTime() <= new Date(checkIn).getTime()) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- sync derived validation: clear invalid checkout when checkin changes
      setCheckOut('');
    }
  }, [checkIn, checkOut]);

  const nights = useMemo(() => {
    if (!checkIn || !checkOut) return 0;
    const diff = new Date(checkOut).getTime() - new Date(checkIn).getTime();
    return Math.max(0, Math.ceil(diff / 86400000));
  }, [checkIn, checkOut]);

  const nightlyRate = selectedRoom.pricePerNight;
  const subtotal = nightlyRate * (nights > 0 ? nights : 1);
  const gstCalc = computeGST(subtotal, 'room', nightlyRate);
  const taxes = gstCalc.total;
  const total = subtotal + taxes;

  /* ---- Hotel not found ---- */
  if (!hotel) {
    return (
      <div id="hotels-detail-not-found" className="flex flex-col items-center justify-center py-32 px-6 text-center">
        <Building2 className="h-16 w-16 text-hms-textDim mb-4" />
        <h2 className="text-xl font-bold text-hms-text mb-2">{t('hotelDetail.notFoundTitle')}</h2>
        <p className="text-sm text-hms-textSub mb-6 max-w-md">{t('hotelDetail.notFoundDescription')}</p>
        <Link id="hotels-detail-not-found-back" href="/hotels" className="inline-flex items-center gap-2 rounded-lg bg-hms-gold px-5 py-2.5 text-sm font-semibold text-black hover:bg-hms-goldLt">
          <ChevronLeft className="h-4 w-4" /> {t('hotelDetail.backToHotels')}
        </Link>
      </div>
    );
  }

  return (
    <div id="hotels-detail-page" className="min-h-screen bg-hms-bg">
      {/* Top bar */}
      <div id="hotels-detail-topbar" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-4 flex items-center justify-between">
        <Link id="hotels-detail-back" href={withQuery('/hotels', resultsQuery)} className="inline-flex items-center gap-1.5 text-sm text-hms-textSub hover:text-hms-gold transition-colors">
          <ChevronLeft size={16} />
          {t('hotelDetail.backToResults')}
        </Link>
        <div className="flex items-center gap-3">
          <button id="hotels-detail-share" type="button" onClick={handleShare} className={cn('inline-flex items-center gap-1.5 text-sm transition-colors', shareStatus === 'copied' ? 'text-hms-green' : shareStatus === 'failed' ? 'text-hms-red' : 'text-hms-textSub hover:text-hms-text')}>
            {shareStatus === 'copied' ? <Check size={16} /> : <Share2 size={16} />}
            <span className="hidden sm:inline">{shareStatus === 'copied' ? t('hotelDetail.copied') : shareStatus === 'failed' ? t('hotelDetail.copyFailed') : t('hotelDetail.share')}</span>
          </button>
          <button id="hotels-detail-save" type="button" onClick={() => toggleSave(slug)} className={cn('inline-flex items-center gap-1.5 text-sm transition-colors', isSaved(slug) ? 'text-red-400' : 'text-hms-textSub hover:text-red-400')}>
            <Heart size={16} className={isSaved(slug) ? 'fill-red-400' : ''} />
            <span className="hidden sm:inline">{isSaved(slug) ? t('hotelDetail.saved') : t('hotelDetail.save')}</span>
          </button>
        </div>
      </div>

      {/* Image gallery */}
      <div id="hotels-gallery" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 overflow-hidden">
          <div id="hotels-detail-gallery-0" className="relative md:col-span-2 h-48 sm:h-64 md:h-[420px] min-h-0 overflow-hidden rounded-xl cursor-pointer" onClick={() => setLightboxIndex(0)}>
            <Image src={galleryImages[0]} alt={`${hotel.name} exterior`} fill sizes="(max-width: 768px) 100vw, 66vw" className="object-cover" priority unoptimized />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
            <button id="hotels-gallery-toggle" type="button" onClick={(e) => { e.stopPropagation(); setShowAllPhotos(!showAllPhotos); }} className="absolute bottom-4 right-4 inline-flex items-center gap-2 rounded-lg bg-hms-bg/80 backdrop-blur-sm border border-hms-border px-4 py-2.5 text-xs font-semibold text-hms-text hover:bg-hms-bg transition-colors">
              <Images size={15} />
              {showAllPhotos ? t('hotelDetail.showLess') : t('hotelDetail.viewAllPhotos')}
            </button>
          </div>
          <div className="hidden md:flex md:flex-col gap-2 min-h-0">
            <div id="hotels-detail-gallery-1" className="relative h-[206px] min-h-0 overflow-hidden rounded-xl cursor-pointer" onClick={() => setLightboxIndex(1)}>
              <Image src={galleryImages[1]} alt={`${hotel.name} room`} fill sizes="33vw" className="object-cover" unoptimized />
            </div>
            <div id="hotels-detail-gallery-2" className="relative h-[206px] min-h-0 overflow-hidden rounded-xl cursor-pointer" onClick={() => setLightboxIndex(2)}>
              <Image src={galleryImages[2]} alt={`${hotel.name} amenities`} fill sizes="33vw" className="object-cover" unoptimized />
            </div>
          </div>
        </div>
      </div>

      {/* Expanded gallery */}
      {showAllPhotos && (
        <div id="hotels-gallery-expanded" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-2">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {hotelGallerySeeds.slice(2).map((src, i) => (
              <div key={i} className="relative h-48 min-h-0 overflow-hidden rounded-xl">
                <Image src={src} alt={`${hotel.name} gallery ${i + 4}`} fill sizes="(max-width: 768px) 50vw, 33vw" className="object-cover" unoptimized />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      <div id="hotels-detail-content" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* Left column */}
          <div id="hotels-detail-info" className="flex-1 min-w-0 space-y-8">
            {/* Hotel heading */}
            <div>
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <span id="hotels-detail-star-badge" className="text-[10px] font-bold uppercase tracking-widest text-hms-gold bg-hms-gold/10 px-2.5 py-1 rounded-full">{t('hotelDetail.starType', { stars: String(hotel.stars), type: hotel.type })}</span>
                <span className="text-[10px] font-medium uppercase tracking-wider text-hms-teal bg-hms-teal/10 px-2.5 py-1 rounded-full">{hotel.rating >= 4.7 ? t('hotelDetail.topRated') : t('hotelDetail.popular')}</span>
              </div>
              <h1 id="hotels-detail-name" className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-hms-text tracking-tight">{hotel.name}</h1>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3">
                <span id="hotels-detail-location" className="inline-flex items-center gap-1.5 text-sm text-hms-textSub">
                  <MapPin size={15} className="text-hms-gold shrink-0" />
                  {hotel.location}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-3">
                <div className="flex items-center gap-2">
                  <StarRating rating={hotel.stars} />
                  <span className="text-sm font-semibold text-hms-gold">{hotel.rating}</span>
                </div>
                <span className="text-sm text-hms-textDim">{t('hotelDetail.verifiedReviews', { count: String(hotel.reviews) })}</span>
              </div>
            </div>

            <hr className="border-hms-border" />

            {/* Description */}
            <div className="space-y-3">
              <h2 id="hotels-detail-about" className="text-lg font-semibold text-hms-text">{t('hotelDetail.aboutThisProperty')}</h2>
              {descriptionParagraphs.map((paragraph) => (
                <p key={paragraph} className="text-sm leading-relaxed text-hms-textSub">
                  {paragraph}
                </p>
              ))}
            </div>

            <hr className="border-hms-border" />

            {/* Amenities */}
            <div className="space-y-4">
              <h2 id="hotels-detail-amenities" className="text-lg font-semibold text-hms-text">{t('hotelDetail.amenitiesAndServices')}</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {hotelAmenities.map((amenity) => (
                  <div key={String(amenity)} className="flex flex-col items-center gap-2 rounded-xl border border-hms-border bg-hms-card px-3 py-4 text-center hover:border-hms-gold/30 transition-colors">
                    <Check size={20} className="text-hms-gold" />
                    <span className="text-[11px] font-medium text-hms-textSub leading-tight">{String(amenity)}</span>
                  </div>
                ))}
              </div>
            </div>

            <hr className="border-hms-border" />

            {/* Room Types */}
            <HotelRoomTypes selectedType={roomType} onSelectType={handleRoomSelect} nights={nights} hotelSlug={slug}
              dateQuery={resultsQuery} />

            <hr className="border-hms-border" />

            {/* Policies */}
            <div className="space-y-4">
              <h2 id="hotels-detail-policies" className="text-lg font-semibold text-hms-text">{t('hotelDetail.hotelPolicies')}</h2>
              <div className="rounded-xl border border-hms-border bg-hms-card p-5 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[[t('hotelDetail.checkIn'), t('hotelDetail.checkInDetail')], [t('hotelDetail.checkOut'), t('hotelDetail.checkOutDetail')]].map(([ti, di]) => (
                    <div key={ti} className="flex items-start gap-3">
                      <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-hms-surface"><Clock size={17} className="text-hms-gold" /></div>
                      <div><p className="text-xs font-semibold text-hms-text">{ti}</p><p className="text-xs text-hms-textSub mt-0.5">{di}</p></div>
                    </div>
                  ))}
                </div>
                <hr className="border-hms-border/50" />
                {[{ icon: CalendarX2, color: 'text-hms-gold', title: t('hotelDetail.cancellationPolicy'), desc: t('hotelDetail.cancellationDetail') },
                  { icon: Info, color: 'text-hms-teal', title: t('hotelDetail.importantInformation'), desc: t('hotelDetail.importantInfoDetail', { amount: formatMoney(60) }) },
                ].map(({ icon: Ic, color, title: ti, desc: di }, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-hms-surface"><Ic size={17} className={color} /></div>
                    <div><p className="text-xs font-semibold text-hms-text">{ti}</p><p className="text-xs text-hms-textSub mt-0.5 leading-relaxed">{di}</p></div>
                  </div>
                ))}
              </div>
            </div>

            <hr className="border-hms-border" />

            {/* Dining & Experiences */}
            <HotelDiningSection checkIn={checkIn} checkOut={checkOut} />

            <hr className="border-hms-border" />

            {/* Reviews */}
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <h2 id="hotels-detail-reviews" className="text-lg font-semibold text-hms-text">{t('hotelDetail.guestReviews')}</h2>
                <div className="flex items-center gap-2">
                  <span className="flex items-center justify-center h-8 w-8 rounded-lg bg-hms-gold text-black text-sm font-bold">{hotel.rating}</span>
                  <div><p className="text-xs font-semibold text-hms-text leading-none">{t('hotelDetail.guestFavorite')}</p><p className="text-[10px] text-hms-textDim mt-0.5">{t('hotelDetail.reviewCount', { count: String(hotel.reviews) })}</p></div>
                </div>
              </div>
              {/* Filter + Sort bar */}
              <div className="flex flex-wrap items-center gap-3">
                <Select id="hotels-detail-reviews-filter" value={String(reviewFilter)} onChange={(e) => setReviewFilter(Number(e.target.value))} placeholder={t('hotelDetail.allRatings')} options={[{label:t('hotelDetail.star5'),value:'5'},{label:t('hotelDetail.star4'),value:'4'},{label:t('hotelDetail.star3'),value:'3'},{label:t('hotelDetail.star2'),value:'2'},{label:t('hotelDetail.star1'),value:'1'}]} />
                <Select id="hotels-detail-reviews-sort" value={reviewSort} onChange={(e) => setReviewSort(e.target.value as 'newest' | 'highest' | 'lowest')} options={[{label:t('hotelDetail.newest'),value:'newest'},{label:t('hotelDetail.highestRated'),value:'highest'},{label:t('hotelDetail.lowestRated'),value:'lowest'}]} />
                <span className="ml-auto text-[10px] text-hms-textDim">{filteredReviews.length} review{filteredReviews.length !== 1 ? t('hotelDetail.s') : ''}</span>
              </div>
              <div className="space-y-4">
                {visibleReviews.map((r) => (
                  <div key={r.name} className="rounded-xl border border-hms-border bg-hms-card p-5 space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-hms-gold/20 to-hms-gold/5 border border-hms-gold/20">
                        <span className="text-xs font-bold text-hms-gold">{r.name.split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase()}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-hms-text truncate">{r.name}{r.verified && <span className="ml-2 inline-flex items-center gap-1 rounded-md bg-hms-teal/10 px-1.5 py-0.5 text-[9px] font-semibold text-hms-teal align-middle"><Check size={9} /> {t('hotelDetail.verified')}</span>}</p>
                        <p className="text-[11px] text-hms-textDim">{r.date}</p>
                      </div>
                      <StarRating rating={r.rating} size={12} />
                    </div>
                    <p className="text-sm leading-relaxed text-hms-textSub">{r.text}</p>
                  </div>
                ))}
              </div>
              {filteredReviews.length > 3 && (
                <button id="hotels-detail-reviews-toggle" type="button" onClick={() => setShowAllReviews(!showAllReviews)} className="w-full rounded-xl border border-hms-border bg-hms-card py-3 text-sm font-medium text-hms-textSub hover:border-hms-borderLt hover:text-hms-text transition-colors">
                  {showAllReviews ? t('hotelDetail.showFewerReviews') : t('hotelDetail.showAllReviews', { count: String(filteredReviews.length) })}
                </button>
              )}
            </div>

            <hr className="border-hms-border" />

            <HotelDetailContactMap hotel={hotel} />

            {/* Similar Hotels */}
            <SimilarHotels hotels={similarHotels} formatMoney={formatMoney} />
          </div>

          {/* Right column -- Booking card */}
          <div id="hotels-detail-sidebar" className="w-full lg:w-[380px] shrink-0">
            <div className="sticky top-24 rounded-2xl border border-hms-border bg-hms-card p-6 space-y-5 shadow-xl shadow-black/20">
              <div className="flex items-baseline gap-2.5 flex-wrap">
                <span className="text-2xl font-bold text-hms-gold">{formatMoney(nightlyRate)}</span>
                <span className="text-sm text-hms-textDim line-through">{formatMoney(selectedRoom.originalPrice)}</span>
                <span className="text-sm text-hms-text">{t('hotelDetail.perNight')}</span>
              </div>
              {hotel.freeCancellation && (
                <div id="hotels-detail-free-cancel" className="flex items-center gap-2 rounded-lg bg-hms-teal/10 px-3 py-2">
                  <Shield size={14} className="shrink-0 text-hms-teal" />
                  <div>
                    <p className="text-xs font-semibold text-hms-teal">{t('hotelDetail.freeCancellation')}</p>
                    <p className="text-[10px] text-hms-textDim">{t('hotelDetail.freeCancelDetail')}</p>
                  </div>
                </div>
              )}
              <hr className="border-hms-border/50" />
              <div className="space-y-4">
                <DateRangePicker
                  id="hotels-detail-dates"
                  label={t('hotelDetail.dateRangeLabel')}
                  startDate={checkIn}
                  endDate={checkOut}
                  minDate={todayStr}
                  onChange={(start, end) => { setCheckIn(start); setCheckOut(end); }}
                />
                <Select id="hotels-detail-guests" label={t('hotelDetail.guests')} value={guests} onChange={(e) => setGuests(e.target.value)} options={[{label:t('hotelDetail.guest1'),value:'1'},{label:t('hotelDetail.guest2'),value:'2'},{label:t('hotelDetail.guest3'),value:'3'},{label:t('hotelDetail.guest4'),value:'4'},{label:t('hotelDetail.guest5'),value:'5'},{label:t('hotelDetail.guest6'),value:'6'}]} />
                <Select id="hotels-detail-room-type" label={t('hotelDetail.roomType')} value={roomType} onChange={(e) => setRoomType(e.target.value)} options={roomTypeOptions} />
              </div>
              <hr className="border-hms-border/50" />
              <div className="space-y-2.5 text-sm">
                {nights > 0 ? (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-hms-textSub">{formatMoney(nightlyRate)} &times; {nights} night{nights !== 1 ? t('hotelDetail.s') : ''}</span>
                      <span className="text-hms-text font-medium">{formatMoney(nightlyRate * nights)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-hms-textSub">{t('hotelDetail.taxesAndFees')}</span>
                      <span className="text-hms-text font-medium">{formatMoney(taxes)}</span>
                    </div>
                    <hr className="border-hms-border/50" />
                    <div className="flex items-center justify-between">
                      <span className="text-hms-text font-semibold">{t('hotelDetail.total')}</span>
                      <span className="text-lg font-bold text-hms-gold transition-all duration-300">{formatMoney(total)}</span>
                    </div>
                  </>
                ) : (
                  <p className="text-xs text-hms-textDim text-center py-2">{t('hotelDetail.selectDatesForPrice')}</p>
                )}
              </div>
              {roomType && (
                <div id="hotels-detail-selected-room-badge" className="flex items-center gap-2 rounded-lg bg-hms-gold/10 border border-hms-gold/25 px-3 py-2">
                  <svg className="w-4 h-4 text-hms-gold flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 7v11a2 2 0 002 2h14a2 2 0 002-2V7M3 7l9-4 9 4" /></svg>
                  <p className="text-[11px] font-semibold text-hms-gold">{t('hotelDetail.roomSelected', { roomType })}</p>
                </div>
              )}
              {checkIn && checkOut ? (
                <Link id="hotels-detail-reserve" href={reserveHref} className="relative flex items-center justify-center w-full h-12 rounded-xl bg-hms-gold text-black text-sm font-bold tracking-wide hover:brightness-110 active:brightness-95 transition-all cursor-pointer shadow-lg shadow-hms-gold/20 overflow-hidden group">
                  <span className="absolute inset-0 rounded-xl bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                  {t('hotelDetail.reserveNow')}
                </Link>
              ) : (
                <div id="hotels-detail-reserve" className="flex items-center justify-center w-full h-12 rounded-xl bg-hms-gold/30 text-black/40 text-sm font-bold tracking-wide cursor-not-allowed">
                  {t('hotelDetail.selectDatesToReserve')}
                </div>
              )}
              <p className="text-center text-[11px] text-hms-textDim">{t('hotelDetail.noChargeYet')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* PhotoLightbox */}
      {lightboxIndex !== null && (
        <PhotoLightbox
          photos={allLightboxPhotos}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}
